import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  LOCALE_ID,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { map, startWith } from 'rxjs';

import { authInterceptorFn, AuthModule } from '@plugins/client/auth';
import { DialogService } from '@plugins/client/dialog/dialog.service';
import { JsonDateModule } from '@plugins/client/json-date';
import { LANGUAGES } from '@plugins/client/routing/app.guard';
import { BrowserLanguageService } from '@plugins/client/services/browser-language.service';
import { translationModuleConfig } from '@plugins/client/translation';
import {
  MiddlewareSDK,
  MiddlewareSdkModule,
  SDK,
} from '@plugins/middleware-sdk';

import { environment } from '../environments/environment';

import { routes } from './app.routes';
import { errorInterceptorFn } from './interceptors/error.interceptor';

function localeIdProvider() {
  return {
    provide: LOCALE_ID,
    deps: [SDK],
    useFactory: async (sdk: MiddlewareSDK) => {
      const locale = await sdk.getPreferredLanguage();
      const lang = locale.replace('-', '_').split('_').at(0);
      if (environment.languages.includes(lang)) {
        return lang;
      }
      return environment.languages.at(0);
    },
  };
}

class LanguageService {
  #translateService = inject(TranslateService);

  readonly locale$ = this.#translateService.onLangChange.pipe(
    map(({ lang }) => lang),
    startWith(this.#translateService.currentLang),
  );
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideAnimations(),
    provideHttpClient(
      withInterceptors([authInterceptorFn, errorInterceptorFn]),
    ),
    importProvidersFrom(MiddlewareSdkModule),
    importProvidersFrom(AuthModule.forRoot()),
    importProvidersFrom(
      TranslateModule.forRoot(
        translationModuleConfig({
          imports: (lang) => [import(`../assets/i18n/common/${lang}.json`)],
        }),
      ),
    ),
    importProvidersFrom(JsonDateModule.configure({ paths: ['/api/'] })),
    localeIdProvider(),
    { provide: LANGUAGES, useValue: environment.languages },
    { provide: BrowserLanguageService, useClass: LanguageService },
    DialogService,
  ],
};
